// features/reports/components/RentabilidadClases.tsx
// ============================================================================
// PUNTO DE EQUILIBRIO Y RENTABILIDAD POR CLASE (bloque B7, 20-oct-2026) — pestaña propia de Reportes, junto a «Ingresos».
// Escrito de Mabel 20-oct (#1484): por clase su ingreso real y proyectado, costo docente (B4, se reutiliza su cálculo y su
// detalle), otros directos (pautas, material/equipo), generales 60 % salón A / 40 % salón B, administrativos entre todas,
// costo por sesión y por alumna, equilibrio directo y completo, utilidad, alertas y un simulador por clase.
// No calcula nada: lo arma el servidor (/finances/presupuesto/rentabilidad, lib/letsmoveRentabilidad.js). Los criterios se
// cambian en «Criterios del reparto» (se guardan en el ajuste rentabilidad_clases del estudio). El simulador NO guarda.
// Plan: forobeta-ops/letsmove/PLAN-FINANZAS-MABEL-BLOQUES-20261014.md (fila B7).
// 21-oct-2026 (respuestas de Mabel + #1499): inscripción aparte, clases sueltas en el ingreso de su clase, recepción de sábados
// repartida entre las sabatinas, «Pauta <clase>» (o «Publicidad <clase>») se asigna sola y botón «Pasar a Excel» (solo si la API lo anuncia: `excel`).
// ============================================================================
import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronLeft, ChevronRight, FileSpreadsheet, HelpCircle, Info, Save, SlidersHorizontal } from 'lucide-react';
import { getRentabilidad, urlExportRentabilidad, Rentabilidad, RBClase, RBConfig, RBTipoGasto } from '../api';
import { moneyEntero as money } from '../../finances/api';
import { putSetting } from '../../../services/settings';
import { Skeleton } from '../../../components/Skeleton';
import { readCache, writeCache } from '../../../services/localCache';
import CostoDocente from './CostoDocente';
import RentabilidadPeriodo from './RentabilidadPeriodo';
import { SelectorVista, VistaPeriodo } from './PeriodoSelector';

const card = 'rounded-2xl border border-line bg-surface p-4 shadow-sm';
const th = 'py-2 pr-2 text-left text-xs font-medium uppercase tracking-wide text-content-3';
const thR = `${th} text-right`;
const td = 'py-1.5 pr-2 text-right tabular-nums';
const navBtn = 'rounded-lg border border-line bg-surface p-2 text-content-2 transition hover:bg-surface-2 disabled:opacity-40';
const input = 'rounded-lg border border-line bg-surface px-2 py-1.5 text-sm text-content outline-none focus:border-brand-blue';
const mesLocal = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Tijuana' }).slice(0, 7);
function sumaMeses(ym: string, n: number) { const [y, m] = ym.split('-').map(Number); const d = new Date(y, m - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; }
const colorU = (v: number) => (v >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400');
const signo = (v: number) => (v < 0 ? `−${money(Math.abs(v))}` : money(v));
/** Columna «Admin.»: administrativos entre todas + la parte sabatina de la recepción (21-oct). */
const adminDe = (c: RBClase) => c.administrativos + (c.recepcionSabados ?? 0);
const NIVEL: Record<string, string> = {
  rojo: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  ambar: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  azul: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
};
const ALERTA_CORTA: Record<string, string> = {
  bajo_directo: 'No cubre su costo directo', bajo_equilibrio: 'Bajo equilibrio', no_alcanza: 'Ni llena alcanza', cupo: 'Cupo',
  margen: 'Margen bajo', sin_alumnas: 'Sin alumnas', archivada: 'Archivada', sin_salon: 'Sin salón',
};
type Filtro = 'todas' | 'alerta' | 'A' | 'B';

// Mes | Trimestre | Semestre (Mabel 23-oct-2026): el trimestre/semestre suma la rentabilidad de cada mes (RentabilidadPeriodo).
export default function RentabilidadClases() {
  const [vistaP, setVistaP] = useState<VistaPeriodo>('mes');
  const [mesP, setMesP] = useState<string>(mesLocal());
  const [movilP, setMovilP] = useState(false);   // ventana móvil (feb–abr…), 24-oct-2026
  return (
    <div className="space-y-4">
      <SelectorVista vista={vistaP} onChange={setVistaP} />
      {vistaP === 'mes' ? <RentabilidadMes /> : <RentabilidadPeriodo tipo={vistaP} mes={mesP} movil={movilP} onMes={(ym, m) => { setMesP(ym); setMovilP(m); }} />}
    </div>
  );
}

function RentabilidadMes() {
  const [mes, setMes] = useState<string>(mesLocal());
  const clave = `rep-rentabilidad-${mes}`;
  const [d, setD] = useState<Rentabilidad | null>(() => readCache<Rentabilidad>(clave) ?? null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<{ status: number; msg: string } | null>(null);
  const [recarga, setRecarga] = useState(0);
  const [abierta, setAbierta] = useState<number | null>(null);
  const [filtro, setFiltro] = useState<Filtro>('todas');
  const [verReparto, setVerReparto] = useState(false);
  const [verCriterios, setVerCriterios] = useState(false);
  const [verDocente, setVerDocente] = useState(false);

  useEffect(() => {
    let vivo = true;
    const cached = readCache<Rentabilidad>(clave);
    setD(cached ?? null); setError(null); setCargando(true);
    getRentabilidad(mes)
      .then((r) => { if (!vivo) return; setD(r); writeCache(clave, r); })
      .catch((e: any) => { if (vivo) setError({ status: Number(e?.status || 0), msg: e?.message || 'No se pudo cargar la rentabilidad' }); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [mes, clave, recarga]);

  const clases = useMemo(() => {
    if (!d) return [];
    return d.clases.filter((c) => filtro === 'todas' ? true : filtro === 'alerta'
      ? c.alertas.some((a) => a.nivel !== 'azul') : c.salon === filtro);
  }, [d, filtro]);

  const esFuturo = mes > sumaMeses(mesLocal(), 1);
  const selector = (
    <div className="flex items-center gap-2">
      <button onClick={() => setMes(sumaMeses(mes, -1))} title="Mes anterior" className={navBtn}><ChevronLeft size={16} /></button>
      <input type="month" value={mes} onChange={(e) => e.target.value && setMes(e.target.value)} className={input} />
      <button onClick={() => setMes(sumaMeses(mes, 1))} disabled={esFuturo} title="Mes siguiente" className={navBtn}><ChevronRight size={16} /></button>
      {mes !== mesLocal() && <button onClick={() => setMes(mesLocal())} className={`${navBtn} text-xs`}>Hoy</button>}
    </div>
  );

  if (error && !d) {
    return (
      <div className="space-y-4">
        {selector}
        <div className={`${card} text-sm`}>
          {error.status === 404
            ? <p className="flex items-center gap-2 text-content-2"><Info size={16} className="text-brand-blue" /> Este reporte se activa cuando el servidor termine de actualizarse. Vuelve a intentar en unos minutos.</p>
            : <p className="text-red-700 dark:text-red-300">{error.msg}</p>}
        </div>
      </div>
    );
  }
  if (!d) return <div className="space-y-4">{selector}<Skeleton className="h-96 w-full rounded-2xl" /></div>;

  const T = d.totales;
  return (
    <div className={`space-y-4 ${cargando ? 'opacity-60 transition' : 'transition'}`} data-testid="rentabilidad">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {selector}
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-content-3"><b className="text-content-2">{d.titulo}</b>{mes === mesLocal() ? ' (mes en curso)' : ''} · {T.clases} clases · {T.alumnas} lugares ocupados</p>
          {d.excel && (
            <a href={urlExportRentabilidad(mes, filtro)} download title="Descargar la tabla tal como se ve (mes y filtro elegidos)" className={`${navBtn} flex items-center gap-1.5 text-sm`} data-testid="rb-excel">
              <FileSpreadsheet size={16} /> Pasar a Excel
            </a>
          )}
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className={card}>
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Ingreso de las clases</div>
          <div className="mt-1 font-display text-xl font-bold text-content">{money(T.ingresoProyectado)}</div>
          <div className="mt-0.5 text-xs text-content-3">proyectado · real {money(T.ingresoReal)}{d.inscripcion?.modo === 'aparte' ? ` · + inscripciones aparte ${money(T.inscripcionAparte ?? 0)}` : ''}</div>
        </div>
        <div className={card}>
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Costo total de las clases</div>
          <div className="mt-1 font-display text-xl font-bold text-content">{money(T.costoTotal)}</div>
          <div className="mt-0.5 text-xs text-content-3">docente {money(T.docente)} · generales {money(T.generales)} · admin. {money(T.administrativos + (T.recepcionSabados ?? 0))}</div>
        </div>
        <div className={card}>
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Utilidad (proyectada)</div>
          <div className={`mt-1 font-display text-xl font-bold ${colorU(T.utilidadProyectada)}`} data-testid="rb-utilidad">{signo(T.utilidadProyectada)}</div>
          <div className="mt-0.5 text-xs text-content-3">con lo cobrado: <span className={colorU(T.utilidadReal)}>{signo(T.utilidadReal)}</span></div>
        </div>
        <div className={card}>
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Clases bajo equilibrio</div>
          <div className={`mt-1 font-display text-xl font-bold ${T.bajoEquilibrio ? 'text-red-700 dark:text-red-300' : 'text-content'}`}>{T.bajoEquilibrio} de {T.conHorario}</div>
          <div className="mt-0.5 text-xs text-content-3">{T.peEstudio != null ? `el estudio necesita ~${T.peEstudio} lugares a ${money(T.precioMedio || 0)}` : ''}</div>
        </div>
      </div>

      {d.dudas.length > 0 && (
        <div className="rounded-xl border border-sky-300 bg-sky-50 p-3 text-sm text-sky-900 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-100" data-testid="rb-dudas">
          <p className="mb-1 flex items-center gap-2 font-semibold"><HelpCircle size={16} /> Supuestos por confirmar (se cambian en «Criterios del reparto»)</p>
          <ul className="ml-6 list-disc space-y-0.5">{d.dudas.map((x) => <li key={x.clave}>{x.texto}</li>)}</ul>
        </div>
      )}
      {d.avisos.length > 0 && (
        <div className="space-y-1 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          {d.avisos.map((a) => <p key={a} className="flex items-start gap-2"><AlertTriangle size={15} className="mt-0.5 shrink-0" /> {a}</p>)}
        </div>
      )}

      {/* Por clase */}
      <div className={card}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-display text-base font-bold text-content">Rentabilidad por clase</h3>
          <div className="inline-flex max-w-full overflow-x-auto rounded-lg border border-line bg-surface p-0.5 text-xs" role="tablist" aria-label="Filtrar">
            {([['todas', 'Todas'], ['alerta', 'Con alerta'], ['A', 'Salón A'], ['B', 'Salón B']] as const).map(([k, l]) => (
              <button key={k} role="tab" aria-selected={filtro === k} onClick={() => setFiltro(k)}
                className={`whitespace-nowrap rounded-md px-2.5 py-1 transition ${filtro === k ? 'bg-accent text-on-accent shadow-sm' : 'text-content-2 hover:text-content'}`}>{l}</button>
            ))}
          </div>
        </div>
        {/* Celular (22-oct-2026, Carlos: «las tablas no se miran completas»): una tarjeta por clase; la tabla de 17 columnas solo desde md. */}
        <div className="mt-2 space-y-2 md:hidden">
          {clases.map((c) => (
            <TarjetaClase key={c.class_id} c={c} abierta={abierta === c.class_id} onToggle={() => setAbierta(abierta === c.class_id ? null : c.class_id)} />
          ))}
          <div className="space-y-1 rounded-xl border border-line p-3 text-sm" data-testid="rb-movil-totales">
            <div className="flex justify-between gap-3 font-semibold text-content"><span>Total {filtro === 'todas' ? '' : '(filtro)'} · {clases.reduce((s, c) => s + c.alumnas, 0)} alumnas</span></div>
            <div className="flex justify-between gap-3 text-content-2"><span>Ingreso proyectado</span><span className="tabular-nums">{money(clases.reduce((s, c) => s + c.ingresoProyectado, 0))}</span></div>
            <div className="flex justify-between gap-3 text-content-2"><span>Ingreso real</span><span className="tabular-nums">{money(clases.reduce((s, c) => s + c.ingresoReal, 0))}</span></div>
            <div className="flex justify-between gap-3 text-content-2"><span>Costo total</span><span className="tabular-nums">{money(clases.reduce((s, c) => s + c.costoTotal, 0))}</span></div>
            <div className="flex justify-between gap-3 font-semibold"><span className="text-content">Utilidad</span><span className={`tabular-nums ${colorU(clases.reduce((s, c) => s + c.utilidadProyectada, 0))}`}>{signo(clases.reduce((s, c) => s + c.utilidadProyectada, 0))}</span></div>
            {filtro === 'todas' && d.inscripcion?.modo === 'aparte' && (
              <div className="flex justify-between gap-3 border-t border-line/60 pt-1 text-content-2"><span>Inscripciones del mes <span className="text-xs text-content-3">(aparte · {d.inscripcion.cobros} cobro{d.inscripcion.cobros === 1 ? '' : 's'})</span></span><span className="tabular-nums">{money(T.inscripcionAparte ?? 0)}</span></div>
            )}
            {filtro === 'todas' && (T.sueltasSinClase ?? 0) > 0 && (
              <div className="flex justify-between gap-3 text-content-2"><span>Clases sueltas sin clase</span><span className="tabular-nums">{money(T.sueltasSinClase ?? 0)}</span></div>
            )}
            {filtro === 'todas' && T.ingresoEstudio && (
              <div className="flex justify-between gap-3 font-semibold text-content"><span>Ingreso del estudio por clases <span className="block text-xs font-normal text-content-3">proyectado · real {money(T.ingresoEstudio.real)}</span></span><span className="tabular-nums">{money(T.ingresoEstudio.proyectado)}</span></div>
            )}
          </div>
        </div>
        <div className="mt-2 hidden overflow-x-auto md:block">
          <table className="w-full min-w-[1100px] text-sm">
            <thead><tr className="border-b border-line">
              <th className={th}>Clase</th><th className={th}>Salón</th><th className={thR}>Ses. / h</th><th className={thR}>Alumnas / cupo</th><th className={thR}>Precio/alumna</th>
              <th className={thR}>Ingreso real</th><th className={thR}>Proyectado</th><th className={thR}>Docente</th><th className={thR}>Otros directos</th>
              <th className={thR}>Generales</th><th className={thR}>Admin.</th><th className={thR}>Costo total</th><th className={thR}>Por sesión</th><th className={thR}>Por alumna</th>
              <th className={thR} title="Alumnas necesarias: directo / completo">Equilibrio</th><th className={thR}>Utilidad</th><th className={th}>Alertas</th>
            </tr></thead>
            <tbody>
              {clases.map((c) => (
                <FilaClase key={c.class_id} c={c} abierta={abierta === c.class_id} onToggle={() => setAbierta(abierta === c.class_id ? null : c.class_id)} />
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-line font-semibold">
                <td className="py-2 pr-2 text-content" colSpan={3}>Total {filtro === 'todas' ? '' : '(filtro)'}</td>
                <td className={`${td} text-content-3`}>{clases.reduce((s, c) => s + c.alumnas, 0)}</td><td />
                <td className={`${td} text-content-2`}>{money(clases.reduce((s, c) => s + c.ingresoReal, 0))}</td>
                <td className={`${td} text-content-2`}>{money(clases.reduce((s, c) => s + c.ingresoProyectado, 0))}</td>
                <td className={`${td} text-content-2`}>{money(clases.reduce((s, c) => s + c.docente.total, 0))}</td>
                <td className={`${td} text-content-2`}>{money(clases.reduce((s, c) => s + c.directos.total, 0))}</td>
                <td className={`${td} text-content-2`}>{money(clases.reduce((s, c) => s + c.generales, 0))}</td>
                <td className={`${td} text-content-2`}>{money(clases.reduce((s, c) => s + adminDe(c), 0))}</td>
                <td className={`${td} text-content`}>{money(clases.reduce((s, c) => s + c.costoTotal, 0))}</td>
                <td colSpan={3} />
                <td className={`${td} ${colorU(clases.reduce((s, c) => s + c.utilidadProyectada, 0))}`}>{signo(clases.reduce((s, c) => s + c.utilidadProyectada, 0))}</td><td />
              </tr>
              {/* Mabel 21-oct: la inscripción NO se reparte por clase → renglón aparte (solo en la vista completa). */}
              {filtro === 'todas' && d.inscripcion?.modo === 'aparte' && (
                <tr className="text-content-2" data-testid="rb-inscripcion-aparte">
                  <td className="py-1.5 pr-2" colSpan={5}>Inscripciones del mes <span className="text-xs text-content-3">(aparte: no se reparten por clase · {d.inscripcion.cobros} cobro{d.inscripcion.cobros === 1 ? '' : 's'})</span></td>
                  <td className={td}>{money(T.inscripcionAparte ?? 0)}</td><td className={td}>{money(T.inscripcionAparte ?? 0)}</td><td colSpan={10} />
                </tr>
              )}
              {filtro === 'todas' && (T.sueltasSinClase ?? 0) > 0 && (
                <tr className="text-content-2">
                  <td className="py-1.5 pr-2" colSpan={5}>Clases sueltas sin clase identificable</td>
                  <td className={td}>{money(T.sueltasSinClase ?? 0)}</td><td className={td}>{money(T.sueltasSinClase ?? 0)}</td><td colSpan={10} />
                </tr>
              )}
              {filtro === 'todas' && T.ingresoEstudio && (
                <tr className="border-t border-line/60 font-semibold text-content">
                  <td className="py-1.5 pr-2" colSpan={5}>Ingreso del estudio por clases <span className="text-xs font-normal text-content-3">(clases + inscripciones + sin clase)</span></td>
                  <td className={td}>{money(T.ingresoEstudio.real)}</td><td className={td}>{money(T.ingresoEstudio.proyectado)}</td><td colSpan={10} />
                </tr>
              )}
            </tfoot>
          </table>
        </div>
        <p className="mt-2 text-xs text-content-3">Toca una clase para ver su desglose y simular cambios (precio, alumnas, costos). Utilidad = ingreso proyectado − costo total. Equilibrio = alumnas necesarias para cubrir el costo directo / el costo total.</p>
      </div>

      {/* Cómo se repartió */}
      <div className={card}>
        <button onClick={() => setVerReparto(!verReparto)} className="flex w-full items-center justify-between gap-2 text-left">
          <h3 className="font-display text-base font-bold text-content">Cómo se repartieron los costos</h3>
          <ChevronDown size={18} className={`text-content-3 transition ${verReparto ? 'rotate-180' : ''}`} />
        </button>
        {verReparto && <Reparto d={d} />}
      </div>

      {/* Criterios */}
      <div className={card}>
        <button onClick={() => setVerCriterios(!verCriterios)} className="flex w-full items-center justify-between gap-2 text-left">
          <h3 className="flex items-center gap-2 font-display text-base font-bold text-content"><SlidersHorizontal size={16} /> Criterios del reparto</h3>
          <ChevronDown size={18} className={`text-content-3 transition ${verCriterios ? 'rotate-180' : ''}`} />
        </button>
        {verCriterios && <Criterios d={d} onGuardado={() => setRecarga((n) => n + 1)} />}
      </div>

      {/* Costo docente (B4) */}
      <div className={card}>
        <button onClick={() => setVerDocente(!verDocente)} className="flex w-full items-center justify-between gap-2 text-left">
          <h3 className="font-display text-base font-bold text-content">Costo docente por clase y nómina pendiente (detalle)</h3>
          <ChevronDown size={18} className={`text-content-3 transition ${verDocente ? 'rotate-180' : ''}`} />
        </button>
        {verDocente && <CostoDocente mes={mes} />}
      </div>

      <div className="text-xs text-content-3">
        {d.supuestos.map((s) => <p key={s}>• {s}</p>)}
        <p className="mt-1">Generado {d.generado}.</p>
      </div>
    </div>
  );
}

function FilaClase({ c, abierta, onToggle }: { c: RBClase; abierta: boolean; onToggle: () => void }) {
  return (
    <>
      <tr className={`cursor-pointer border-b border-line/60 hover:bg-surface-2 ${abierta ? 'bg-surface-2' : ''}`} onClick={onToggle} data-testid={`rb-clase-${c.class_id}`}>
        <td className="py-1.5 pr-2">
          <div className="text-content">{c.nombre}</div>
          <div className="text-xs text-content-3">{c.maestros || '—'}</div>
        </td>
        <td className="py-1.5 pr-2 text-content-2">{c.salon ?? '—'}<div className="text-[11px] text-content-3">{c.salonNombre ?? ''}</div></td>
        <td className={`${td} text-content-3`}>{c.sesiones} / {c.horas}</td>
        <td className={`${td} text-content-2`}>{c.alumnas}{c.capacidad ? <span className="text-content-3"> / {c.capacidad}</span> : ''}</td>
        <td className={`${td} text-content-2`}>{c.precioAlumna != null ? money(c.precioAlumna) : '—'}</td>
        <td className={`${td} text-content-2`}>{money(c.ingresoReal)}</td>
        <td className={`${td} text-content-2`}>{money(c.ingresoProyectado)}</td>
        <td className={`${td} text-content-2`}>{money(c.docente.total)}</td>
        <td className={`${td} text-content-2`}>{c.directos.total ? money(c.directos.total) : '—'}</td>
        <td className={`${td} text-content-2`}>{money(c.generales)}</td>
        <td className={`${td} text-content-2`}>{money(adminDe(c))}</td>
        <td className={`${td} font-semibold text-content`}>{money(c.costoTotal)}</td>
        <td className={`${td} text-content-3`}>{c.porSesion != null ? money(c.porSesion) : '—'}</td>
        <td className={`${td} text-content-3`}>{c.porAlumna != null ? money(c.porAlumna) : '—'}</td>
        <td className={`${td} text-content-2`}>{c.peDirecto ?? '—'} / {c.peCompleto ?? '—'}</td>
        <td className={`${td} font-semibold ${colorU(c.utilidadProyectada)}`}>{signo(c.utilidadProyectada)}{c.margenPct != null && <div className="text-[11px] font-normal text-content-3">{c.margenPct} %</div>}</td>
        <td className="py-1.5">
          <div className="flex flex-wrap gap-1">{c.alertas.map((a) => <span key={a.tipo} title={a.texto} className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${NIVEL[a.nivel]}`}>{ALERTA_CORTA[a.tipo] || a.tipo}</span>)}</div>
        </td>
      </tr>
      {abierta && (
        <tr className="border-b border-line bg-surface-2/50"><td colSpan={17} className="p-3"><Detalle c={c} /></td></tr>
      )}
    </>
  );
}

function TarjetaClase({ c, abierta, onToggle }: { c: RBClase; abierta: boolean; onToggle: () => void }) {
  const dato = (l: string, v: React.ReactNode) => (
    <div><div className="text-[11px] uppercase tracking-wide text-content-3">{l}</div><div className="tabular-nums text-content-2">{v}</div></div>
  );
  return (
    <div className={`rounded-xl border border-line ${abierta ? 'bg-surface-2' : ''}`}>
      <button type="button" onClick={onToggle} className="w-full p-3 text-left" data-testid={`rb-movil-clase-${c.class_id}`} aria-expanded={abierta}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-content">{c.nombre}</div>
            <div className="text-xs text-content-3">{c.maestros || '—'} · Salón {c.salon ?? '—'}{c.salonNombre ? ` (${c.salonNombre})` : ''}</div>
          </div>
          <div className={`shrink-0 text-right font-semibold tabular-nums ${colorU(c.utilidadProyectada)}`}>
            {signo(c.utilidadProyectada)}
            {c.margenPct != null && <div className="text-[11px] font-normal text-content-3">{c.margenPct} %</div>}
          </div>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-x-3 gap-y-2 text-sm">
          {dato('Alumnas', <>{c.alumnas}{c.capacidad ? <span className="text-content-3"> / {c.capacidad}</span> : ''}</>)}
          {dato('Equilibrio', `${c.peDirecto ?? '—'} / ${c.peCompleto ?? '—'}`)}
          {dato('Precio', c.precioAlumna != null ? money(c.precioAlumna) : '—')}
          {dato('Proyectado', money(c.ingresoProyectado))}
          {dato('Costo total', money(c.costoTotal))}
          {dato('Ses. / h', `${c.sesiones} / ${c.horas}`)}
        </div>
        {c.alertas.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">{c.alertas.map((a) => <span key={a.tipo} title={a.texto} className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${NIVEL[a.nivel]}`}>{ALERTA_CORTA[a.tipo] || a.tipo}</span>)}</div>
        )}
      </button>
      {abierta && <div className="border-t border-line p-3"><Detalle c={c} /></div>}
    </div>
  );
}

function Detalle({ c }: { c: RBClase }) {
  const [precio, setPrecio] = useState(String(c.precioAlumna ?? ''));
  const [alumnas, setAlumnas] = useState(String(c.alumnas));
  const [cap, setCap] = useState(String(c.capacidad ?? ''));
  const [docente, setDocente] = useState(String(c.docente.total));
  const [directos, setDirectos] = useState(String(c.directos.total));
  const [asignados, setAsignados] = useState(String(Math.round((c.generales + adminDe(c)) * 100) / 100));
  const n = (s: string) => (Number.isFinite(Number(s)) ? Number(s) : 0);
  const P = n(precio), A = n(alumnas), C = n(cap), cDir = n(docente) + n(directos), cTot = cDir + n(asignados);
  const ingreso = P * A, util = ingreso - cTot;
  const peD = P > 0 ? Math.ceil(cDir / P - 1e-9) : null, peC = P > 0 ? Math.ceil(cTot / P - 1e-9) : null;
  const linea = (l: string, v: number, extra?: string) => (
    <div className="flex justify-between gap-3 py-0.5"><span className="text-content-2">{l}{extra ? <span className="text-xs text-content-3"> · {extra}</span> : ''}</span><span className="whitespace-nowrap tabular-nums text-content">{signo(v)}</span></div>
  );
  return (
    <div className="space-y-4 text-sm md:sticky md:left-0 md:w-[min(46rem,calc(100vw-4rem))]">
      <div>
        <h4 className="mb-1 font-semibold text-content">Desglose de {c.nombre}</h4>
        {c.alertas.map((a) => <p key={a.tipo} className={`mb-1 inline-block rounded px-2 py-0.5 text-xs ${NIVEL[a.nivel]}`}>{a.texto}</p>)}
        {linea('Ingreso real (cobrado del mes)', c.ingresoReal, `${c.alumnasPagan} pagaron`)}
        {linea('Ingreso proyectado (plan − descuento − beca)', c.ingresoProyectado, `${c.alumnasProyectadas} alumnas${c.inscripcion ? ` · incluye ${money(c.inscripcion)} de inscripción` : ''}`)}
        {c.sueltas ? linea('… de ello, clases sueltas de esta clase', c.sueltas, `${c.nSueltas ?? 0} cobro(s)`) : null}
        {linea('Docente · por hora', -c.docente.porHora)}
        {c.docente.sueldoFijo ? linea('Docente · sueldo fijo (parte de esta clase)', -c.docente.sueldoFijo) : null}
        {c.docente.comision ? linea('Docente · comisión', -c.docente.comision) : null}
        {c.docente.bonos ? linea('Docente · bonos y ajustes (por horas)', -c.docente.bonos) : null}
        {c.docente.recepcion ? linea('Docente · menos recepción de sábados (va a administrativos y a las clases sabatinas)', -c.docente.recepcion) : null}
        {c.directos.gastos.map((g, i) => <div key={i}>{linea(`Directo · ${g.concepto}`, -g.monto, g.clases > 1 ? `1/${g.clases} de ${money(g.de)}` : undefined)}</div>)}
        {linea(`Gastos generales (salón ${c.salon ?? '—'})`, -c.generales)}
        {linea('Administrativos (entre todas las clases)', -c.administrativos)}
        {c.recepcionSabados ? linea('Recepción de sábados (solo clases sabatinas)', -c.recepcionSabados) : null}
        <div className="mt-1 border-t border-line pt-1">{linea('Utilidad proyectada', c.utilidadProyectada, c.margenPct != null ? `margen ${c.margenPct} %` : undefined)}</div>
        <p className="mt-1 text-xs text-content-3">Equilibrio directo: {c.peDirecto ?? '—'} alumnas · completo: {c.peCompleto ?? '—'} alumnas · ingreso mínimo {money(c.ingresoMinimo)}{c.precioEstimado ? ' · precio estimado con el promedio del estudio (la clase no tiene alumnas que paguen)' : ''}.</p>
      </div>
      <div className="rounded-xl border border-line bg-surface p-3">
        <h4 className="mb-2 font-semibold text-content">Simulador (no guarda nada)</h4>
        <div className="grid grid-cols-2 gap-3 md:flex md:flex-wrap">
          {([['Precio por alumna', precio, setPrecio], ['Alumnas', alumnas, setAlumnas], ['Cupo', cap, setCap], ['Costo docente', docente, setDocente], ['Otros directos', directos, setDirectos], ['Generales + admin.', asignados, setAsignados]] as const).map(([l, v, set]) => (
            <label key={l} className="flex flex-col text-xs text-content-3">{l}
              <input type="number" inputMode="decimal" value={v} onChange={(e) => set(e.target.value)} onClick={(e) => e.stopPropagation()} className={`${input} mt-0.5 w-full md:w-32`} />
            </label>
          ))}
        </div>
        <p className="mt-2 text-sm text-content-2">
          Ingreso {money(ingreso)} − costo {money(cTot)} = <b className={colorU(util)}>{signo(util)}</b>
          {' · '}equilibrio directo <b>{peD ?? '—'}</b> · completo <b>{peC ?? '—'}</b> alumnas
          {C > 0 && peC != null && peC > C && <span className="ml-1 rounded bg-red-100 px-1.5 text-xs text-red-800 dark:bg-red-900/40 dark:text-red-200">ni llena alcanza el equilibrio</span>}
          {C > 0 && A > C && <span className="ml-1 rounded bg-amber-100 px-1.5 text-xs text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">más alumnas que cupo</span>}
        </p>
      </div>
    </div>
  );
}

function Reparto({ d }: { d: Rentabilidad }) {
  const T = d.totales;
  const lista = (titulo: string, items: { concepto: string; monto: number; motivo?: string }[], total: number) => (
    <div className="mt-3">
      <div className="flex justify-between font-semibold text-content"><span>{titulo}</span><span className="tabular-nums">{money(total)}</span></div>
      <ul className="mt-1 divide-y divide-line/50 text-xs">
        {items.map((x, i) => <li key={i} className="flex justify-between gap-3 py-0.5 text-content-2"><span>{x.concepto}{x.motivo ? <span className="text-content-3"> · {x.motivo}</span> : ''}</span><span className="shrink-0 tabular-nums">{money(x.monto)}</span></li>)}
        {!items.length && <li className="py-0.5 text-content-3">—</li>}
      </ul>
    </div>
  );
  return (
    <div className="mt-2 text-sm">
      <p className="text-content-3">Cada peso cuenta una sola vez: lo asignado a las clases ({money(T.costoTotal)}) + lo que queda fuera ({money(T.fuera)}) = nómina calculada ({money(T.nominaDocente + T.nominaAdmin)}) + gastos del mes sin nómina ({money(T.gastosMes)}){Math.abs(T.cuadre) < 0.02 ? ' ✓' : ` (diferencia ${money(T.cuadre)})`}. Los {T.nominaRegistradaGastos.n} gastos de nómina registrados ({money(T.nominaRegistradaGastos.monto)}) no se suman otra vez: ya van en la nómina calculada.</p>
      <div className="mt-3">
        <div className="font-semibold text-content">Gastos generales {money(T.generalPool)} → por salón</div>
        <ul className="mt-1 text-xs text-content-2">{d.salones.map((s) => <li key={s.salon}>Salón {s.salon} ({s.salones.join(', ') || 'sin salón asignado'}): {s.pct} % = {money(s.monto)} entre {s.clases} clases ({s.horas} h)</li>)}</ul>
      </div>
      {lista('Generales (60/40)', d.generales.detalle, d.generales.total)}
      {lista('Administrativos (entre todas las clases)', d.administrativos.detalle, d.administrativos.total)}
      {d.sabados && lista('Recepción de sábados (solo clases sabatinas)', d.sabados.clases.map((c) => ({ concepto: c.nombre, monto: c.monto })), d.sabados.total)}
      {lista('Fuera del cálculo por clase', d.fuera, T.fuera)}
      {T.ingresoSinClase.proyectado > 0 && <p className="mt-3 text-xs text-content-3">Ingreso sin clase (alumnas sin clase inscrita): real {money(T.ingresoSinClase.real)}, proyectado {money(T.ingresoSinClase.proyectado)} — no entra a ninguna clase.</p>}
    </div>
  );
}

function Criterios({ d, onGuardado }: { d: Rentabilidad; onGuardado: () => void }) {
  const [cfg, setCfg] = useState<RBConfig>(() => ({ ...d.config, salones: Object.fromEntries(d.salonesDisponibles.map((s) => [String(s.classroom_id), s.salon])) }));
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; t: string } | null>(null);
  const set = <K extends keyof RBConfig>(k: K, v: RBConfig[K]) => setCfg((c) => ({ ...c, [k]: v }));
  const tipoDe = (cid: number | null, actual: RBTipoGasto) => (cid != null && cfg.conceptos[String(cid)]) || actual;
  const asignables = useMemo(() => {
    const out: { expense_id: number; concepto: string; monto: number; tipo: RBTipoGasto }[] = [];
    const vistos = new Set<number>();
    const add = (x: { expense_id?: number; concepto: string; monto: number; tipo?: RBTipoGasto }) => { if (x.expense_id && !vistos.has(x.expense_id) && x.tipo && ['pauta', 'directo', 'inversion'].includes(x.tipo)) { vistos.add(x.expense_id); out.push({ expense_id: x.expense_id, concepto: x.concepto, monto: x.monto, tipo: x.tipo }); } };
    [...d.generales.detalle, ...d.administrativos.detalle, ...d.fuera].forEach(add);
    d.clases.forEach((c) => c.directos.gastos.forEach((g) => add({ expense_id: g.expense_id, concepto: g.concepto, monto: g.de, tipo: g.tipo })));
    return out;
  }, [d]);
  const clasesOrden = useMemo(() => [...d.clases].sort((a, b) => a.nombre.localeCompare(b.nombre)), [d]);
  // Gastos que ya van a su clase por el concepto «Pauta <clase>» / «Publicidad <clase>» (21-oct): expense_id → clases.
  const porConcepto = useMemo(() => {
    const m = new Map<number, string[]>();
    d.clases.forEach((c) => c.directos.gastos.forEach((g) => { if (g.porConcepto) m.set(g.expense_id, [...(m.get(g.expense_id) || []), c.nombre]); }));
    return m;
  }, [d]);

  async function guardar() {
    setGuardando(true); setMsg(null);
    try {
      await putSetting('rentabilidad_clases', JSON.stringify(cfg));
      setMsg({ ok: true, t: 'Guardado. Recalculando…' }); onGuardado();
    } catch (e: any) { setMsg({ ok: false, t: e?.message || 'No se pudo guardar' }); }
    finally { setGuardando(false); }
  }
  const sel = (v: string, on: (v: string) => void, ops: [string, string][]) => (
    <select value={v} onChange={(e) => on(e.target.value)} className={input}>{ops.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
  );
  return (
    <div className="mt-3 space-y-5 text-sm" data-testid="rb-criterios">
      <section>
        <h4 className="font-semibold text-content">Salones</h4>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          {d.salonesDisponibles.map((s) => (
            <label key={s.classroom_id} className="flex items-center gap-1.5 text-content-2">{s.nombre}
              {sel(cfg.salones?.[String(s.classroom_id)] ?? '', (v) => set('salones', { ...(cfg.salones || {}), [String(s.classroom_id)]: (v || null) as 'A' | 'B' | null }), [['A', 'A (grande)'], ['B', 'B (chico)'], ['', 'Ninguno']])}
            </label>
          ))}
          <label className="flex items-center gap-1.5 text-content-2">% salón A
            <input type="number" min={0} max={100} value={cfg.pctA} onChange={(e) => set('pctA', Number(e.target.value))} className={`${input} w-20`} />
            <span className="text-content-3">→ B {Math.round((100 - Number(cfg.pctA || 0)) * 100) / 100} %</span>
          </label>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-content-2">
          <label className="flex items-center gap-1.5">Dentro de cada salón {sel(cfg.repartoSalon, (v) => set('repartoSalon', v as RBConfig['repartoSalon']), [['horas', 'por horas de clase'], ['iguales', 'partes iguales']])}</label>
          <label className="flex items-center gap-1.5">Administrativos entre todas {sel(cfg.repartoAdmin, (v) => set('repartoAdmin', v as RBConfig['repartoAdmin']), [['horas', 'por horas de clase'], ['iguales', 'partes iguales']])}</label>
        </div>
      </section>

      <section>
        <h4 className="font-semibold text-content">Tipo de cada gasto del mes</h4>
        {/* Celular: un renglón por concepto con su selector debajo (la tabla medía 792 px). */}
        <div className="mt-1 divide-y divide-line/50 md:hidden" data-testid="rb-movil-conceptos">
          {d.conceptos.filter((k) => k.concept_id != null).map((k) => (
            <div key={k.concept_id!} className="py-2">
              <div className="flex justify-between gap-3"><span className="text-content">{k.concepto}</span><span className="shrink-0 tabular-nums text-content-2">{money(k.monto)}</span></div>
              <div className="text-xs text-content-3">{k.categoria}</div>
              <div className="mt-1 [&>select]:w-full">{sel(tipoDe(k.concept_id, k.tipo), (v) => set('conceptos', { ...cfg.conceptos, [String(k.concept_id)]: v as RBTipoGasto }), d.tipos.map((t) => [t.clave, t.texto]))}</div>
            </div>
          ))}
        </div>
        <div className="mt-1 hidden overflow-x-auto md:block">
          <table className="w-full min-w-[560px] text-sm">
            <thead><tr className="border-b border-line"><th className={th}>Concepto</th><th className={th}>Categoría</th><th className={thR}>Este mes</th><th className={th}>Va a</th></tr></thead>
            <tbody>
              {d.conceptos.filter((k) => k.concept_id != null).map((k) => (
                <tr key={k.concept_id!} className="border-b border-line/50">
                  <td className="py-1 pr-2 text-content">{k.concepto}</td><td className="py-1 pr-2 text-content-3">{k.categoria}</td>
                  <td className={`${td} text-content-2`}>{money(k.monto)}</td>
                  <td className="py-1">{sel(tipoDe(k.concept_id, k.tipo), (v) => set('conceptos', { ...cfg.conceptos, [String(k.concept_id)]: v as RBTipoGasto }), d.tipos.map((t) => [t.clave, t.texto]))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <label className="mt-2 flex flex-wrap items-center gap-1.5 text-content-2">Pauta sin clase asignada: {sel(cfg.pautaSinClase, (v) => set('pautaSinClase', v as RBConfig['pautaSinClase']), [['todas', 'entre todas las clases'], ['general', 'como gasto general 60/40'], ['fuera', 'fuera del cálculo']])}</label>
      </section>

      <section>
        <h4 className="font-semibold text-content">Pautas, material y equipo de este mes → ¿a qué clase?</h4>
        <p className="text-xs text-content-3">Si el gasto se captura con un concepto «Pauta &lt;nombre de la clase&gt;» (p. ej. «Pauta Active Program»), se carga solo a esa clase.</p>
        {!asignables.length && <p className="text-content-3">No hay gastos de pauta, material o inversión este mes.</p>}
        <ul className="mt-1 space-y-2">
          {asignables.map((g) => {
            const elegidas = cfg.asignaciones[String(g.expense_id)] || [];
            return (
              <li key={g.expense_id} className="rounded-lg border border-line p-2">
                <div className="flex justify-between gap-2 text-content"><span>{g.concepto}</span><span className="tabular-nums">{money(g.monto)}</span></div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {clasesOrden.map((c) => {
                    const on = elegidas.includes(c.class_id);
                    return (
                      <button key={c.class_id} type="button"
                        onClick={() => set('asignaciones', { ...cfg.asignaciones, [String(g.expense_id)]: on ? elegidas.filter((x) => x !== c.class_id) : [...elegidas, c.class_id] })}
                        className={`rounded-full border px-2 py-0.5 text-xs ${on ? 'border-accent bg-accent text-on-accent' : 'border-line text-content-2 hover:bg-surface-2'}`}>{c.nombre}</button>
                    );
                  })}
                </div>
                <p className="mt-1 text-xs text-content-3">{elegidas.length ? `Se reparte a partes iguales entre ${elegidas.length} clase(s).` : porConcepto.has(g.expense_id) ? `Por su concepto va a: ${porConcepto.get(g.expense_id)!.join(', ')}.` : g.tipo === 'pauta' ? 'Sin clase: va según «Pauta sin clase asignada».' : g.tipo === 'directo' ? 'Sin clase: va a gastos generales.' : 'Sin clase: queda fuera del cálculo.'}</p>
              </li>
            );
          })}
        </ul>
      </section>

      <section>
        <h4 className="font-semibold text-content">Nómina</h4>
        <div className="mt-1 flex flex-wrap items-center gap-3 text-content-2">
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={!!cfg.recepcion} onChange={(e) => set('recepcion', e.target.checked ? { user_id: d.recepcion?.user_id || 466, semanal: d.recepcion?.semanal || 350, destino: 'administrativo', sabados: { semanal: d.recepcion?.sabados?.semanal ?? 350, clases: null } } : null)} />
            Recepción de sábados sale del sueldo de
          </label>
          {cfg.recepcion && (
            <>
              {sel(String(cfg.recepcion.user_id), (v) => set('recepcion', { ...cfg.recepcion!, user_id: Number(v) }),
                [...new Map<string, string>([...d.docentes.map((p) => [String(p.user_id), p.nombre] as [string, string]),
                  ...(d.recepcion ? [[String(d.recepcion.user_id), d.recepcion.nombre] as [string, string]] : [])]).entries()])}
              <label className="flex items-center gap-1.5">$ por semana <input type="number" min={0} value={cfg.recepcion.semanal} onChange={(e) => set('recepcion', { ...cfg.recepcion!, semanal: Number(e.target.value) })} className={`${input} w-24`} /></label>
              {sel(cfg.recepcion.destino, (v) => set('recepcion', { ...cfg.recepcion!, destino: v as 'administrativo' | 'fuera' | 'clases' }), [['administrativo', 'como administrativo (todas las clases)'], ['fuera', 'fuera del cálculo'], ['clases', 'se queda en sus clases']])}
              <label className="flex items-center gap-1.5">+ $ por semana solo a las clases sabatinas <input type="number" min={0} value={cfg.recepcion.sabados?.semanal ?? 0} onChange={(e) => set('recepcion', { ...cfg.recepcion!, sabados: { semanal: Number(e.target.value), clases: cfg.recepcion!.sabados?.clases ?? null } })} className={`${input} w-24`} /></label>
            </>
          )}
        </div>
        {cfg.recepcion && d.recepcion?.sabados && (
          <p className="mt-1 text-xs text-content-3">Clases sabatinas este mes: {d.recepcion.sabados.clases.map((c) => c.nombre).join(', ') || '—'}{d.recepcion.sabados.auto ? ' (las que solo se dan en sábado)' : ''}. El resto del sueldo de {d.recepcion.nombre} se queda en su clase{d.recepcion.restoASuClase != null ? ` (${money(d.recepcion.restoASuClase)} este mes)` : ''}.</p>
        )}
        <label className="mt-2 flex items-center gap-1.5 text-content-2"><input type="checkbox" checked={cfg.bonosAClases} onChange={(e) => set('bonosAClases', e.target.checked)} /> Bonos y ajustes de cada maestro van a sus clases (por horas); si no, a administrativos</label>
      </section>

      <section>
        <h4 className="font-semibold text-content">Ingreso y alertas</h4>
        <div className="mt-1 flex flex-wrap items-center gap-3 text-content-2">
          <label className="flex items-center gap-1.5">Inscripción {sel(cfg.inscripcion, (v) => set('inscripcion', v as RBConfig['inscripcion']), [['aparte', 'aparte (no se reparte por clase)'], ['semestre', '1/6 por mes a sus clases (1 vez por semestre)'], ['no', 'no se toma en cuenta']])}</label>
          <label className="flex items-center gap-1.5">Avisar cupo desde <input type="number" min={1} max={100} value={cfg.alertas.cupoPct} onChange={(e) => set('alertas', { ...cfg.alertas, cupoPct: Number(e.target.value) })} className={`${input} w-20`} /> %</label>
          <label className="flex items-center gap-1.5">Margen mínimo <input type="number" min={-100} max={100} value={cfg.alertas.margenMinPct} onChange={(e) => set('alertas', { ...cfg.alertas, margenMinPct: Number(e.target.value) })} className={`${input} w-20`} /> %</label>
        </div>
      </section>

      <div className="flex flex-wrap items-center gap-3">
        <button onClick={guardar} disabled={guardando} className="flex items-center gap-1.5 rounded-lg bg-accent px-3 py-2 text-sm font-medium text-on-accent shadow-sm disabled:opacity-50" data-testid="rb-guardar">
          <Save size={15} /> {guardando ? 'Guardando…' : 'Guardar criterios'}
        </button>
        {msg && <span className={msg.ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-300'}>{msg.t}</span>}
        <span className="text-xs text-content-3">Aplica a todos los meses. Solo quien administra Finanzas puede guardarlos.</span>
      </div>
    </div>
  );
}
